<template>
  <div>
    <!-- Overlay for mobile -->
    <div
      @click="emit('update:modelValue', false)"
      :class="[
        'fixed inset-0 z-40 transition-opacity duration-300',
        props.modelValue ? 'opacity-100 bg-gray-850/50 lg:opacity-0 lg:pointer-events-none' : 'pointer-events-none opacity-0'
      ]"
    />

    <!-- Sidebar container -->
    <aside
      :class="[
        'w-sidebar h-screen bg-gray-850 border-r border-gray-550 fixed left-0 top-0 z-50 flex flex-col pl-3 pr-4 py-4 transition-transform duration-300',
        props.modelValue ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      ]"
    >
      <!-- Header: Logo -->
      <div class="flex items-center justify-between mb-4">
        <b-logo variant="black" class="h-5 w-10 cursor-pointer" @click="router.push('/')" />
      </div>

      <!-- Profile Section -->
      <b-navbar-profile />

      <!-- Divider -->
      <div class="h-px bg-gray-650 my-4" />

      <!-- Freelance Navigation -->
      <template v-if="!isOrganization">
        <!-- Section: MES MISSIONS -->
        <div class="flex flex-col">
          <span class="text-xs font-medium text-gray-450 uppercase tracking-wide pl-4 mb-2">
            {{ $t('sidebar.section_missions') }}
          </span>

          <div class="flex flex-col gap-1">
            <b-sidebar-item
              to="/pass-d"
              :label="$t('sidebar.pass_d')"
              :active="route.path.startsWith('/pass-d')"
              :badge-count="invitedCount"
              data-e2e="sidebar-passd-link"
            />

            <b-sidebar-item
              to="/missions"
              :label="$t('sidebar.missions')"
              :active="route.path.startsWith('/mission')"
              :badge-count="missionOffersCount"
              data-e2e="sidebar-missions-link"
            />

            <b-sidebar-item
              to="/accounting"
              :label="$t('sidebar.invoicing')"
              :active="route.path.startsWith('/accounting') && !route.path.startsWith('/accounting/company')"
            />

            <b-sidebar-item
              to="/referral"
              :label="$t('sidebar.invite_experts')"
              :active="route.path.startsWith('/referral')"
              prepend-icon="solar:users-group-rounded-bold-duotone"
              data-e2e="sidebar-invite-experts-link"
            />

          </div>
        </div>
      </template>

      <!-- Organization Navigation -->
      <template v-else>
        <!-- Section: EXPERTS -->
        <div class="flex flex-col">
          <span class="text-xs font-medium text-gray-450 uppercase tracking-wide pl-4 mb-2">
            {{ $t('sidebar.section_experts') }}
          </span>

          <div class="flex flex-col gap-1">
            <b-sidebar-item
              to="/company/experts/search"
              :label="$t('sidebar.find_expert')"
              :active="route.path.startsWith('/company/experts/search')"
              prepend-icon="solar:magnifer-linear"
              data-e2e="sidebar-expert-search-link"
            />

            <b-sidebar-item
              to="/company/experts"
              :label="$t('sidebar.my_experts')"
              :active="route.path.startsWith('/company/experts') && !route.path.includes('/search')"
              prepend-icon="solar:bookmark-linear"
            />

          </div>
        </div>

        <!-- Divider -->
        <div class="h-px bg-gray-650 my-4" />

        <!-- Section: MISSIONS -->
        <div class="flex flex-col">
          <span class="text-xs font-medium text-gray-450 uppercase tracking-wide pl-4 mb-2">
            {{ $t('sidebar.section_missions') }}
          </span>

          <div class="flex flex-col gap-1">
            <b-sidebar-item
              to="/company/missions"
              :label="$t('sidebar.my_missions')"
              :active="route.path.startsWith('/company/missions')"
              prepend-icon="solar:suitcase-linear"
              data-e2e="sidebar-company-missions-link"
            />

            <b-sidebar-item
              to="/company/accounting"
              :label="$t('sidebar.invoices_payments')"
              :active="route.path.startsWith('/company/accounting')"
              prepend-icon="solar:euro-linear"
            />
          </div>
        </div>
      </template>

      <!-- Admin Section - Only visible for admin users -->
      <template v-if="isAdmin">
        <div class="h-px bg-gray-650 my-4" />

        <div class="flex flex-col">
          <span class="text-xs font-medium text-gray-450 uppercase tracking-wide pl-4 mb-2">
            {{ $t('sidebar.section_admin') }}
          </span>

          <div class="flex flex-col gap-1">
            <b-sidebar-item
              to="/admin"
              :label="$t('sidebar.admin')"
              :active="route.path.startsWith('/admin')"
              prepend-icon="solar:shield-keyhole-bold-duotone"
            />
          </div>
        </div>
      </template>

      <!-- Spacer -->
      <div class="flex-1" />

      <!-- Bottom Menu -->
      <div class="flex flex-col gap-1 mb-4">
        <b-sidebar-item
          to="/settings"
          :label="$t('sidebar.my_account')"
          :active="route.path.startsWith('/settings')"
          prepend-icon="solar:settings-bold-duotone"
          data-e2e="sidebar-settings-link"
        />

        <b-sidebar-item
          to="mailto:support@weplaybig.com?subject=Support%20Request"
          :label="$t('sidebar.support')"
          :active="false"
          prepend-icon="solar:question-circle-bold-duotone"
        />
      </div>

      <!-- Profile Completion Card (temporarily disabled)
      <b-profile-completion-indicator-card />
      -->

      <!-- Footer: Legal -->
      <div class="mt-4">
        <div class="h-px bg-gray-650 mb-4" />
        <NuxtLink
          to="/mentions-legales"
          class="block text-sm text-text-secondary px-4 py-2 hover:text-gray-300 transition-colors"
        >
          {{ $t('sidebar.legal_notice') }}
        </NuxtLink>
        <NuxtLink
          to="/terms-and-conditions"
          class="block text-sm text-text-secondary px-4 py-2 hover:text-gray-300 transition-colors"
        >
          {{ $t('sidebar.terms_and_conditions') }}
        </NuxtLink>
        <NuxtLink
          to="/confidentiality"
          class="block text-sm text-text-secondary px-4 py-2 hover:text-gray-300 transition-colors"
        >
          {{ $t('sidebar.privacy_policy') }}
        </NuxtLink>
      </div>
    </aside>
  </div>
</template>

<script lang="ts" setup>
import BSidebarItem from "~/components/navigation/BSidebarItem.vue";
import BNavbarProfile from "~/components/navigation/BNavbarProfile.vue";
import { authStore } from "~/stores/auth";
import { useInvitationStore } from "~/stores/invitation";

const route = useRoute();
const router = useRouter();

// Check if user is admin or organization
const auth = authStore();
const isAdmin = computed(() => auth.isAdmin);
const isOrganization = computed(() => auth.isOrganization);

// Mission offers count (placeholder - can be connected to a store)
const missionOffersCount = ref(0);

// Expert-side count badge on the "Pass'D" item: how many mission invitations
// are awaiting the current expert's response (mission still open). Replaces the
// old full-width banner (ticket #154, Jim's feedback on placement/visibility).
// The sidebar only mounts for authenticated users (guarded in the layout).
const invitationStore = useInvitationStore();
const { invitedCount } = storeToRefs(invitationStore);

onMounted(() => {
  if (!isOrganization.value) {
    invitationStore.fetchMyInvitedCount();
  }
});

interface Props {
  modelValue: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits(['update:modelValue']);
</script>
